// src/components/superadmin/CampusDashboard/StaffViewModal.jsx
import React from "react";
import { MdClose, MdPerson, MdEmail, MdPhone, MdHome, MdWork, MdAttachMoney, MdCalendarToday, MdEmergency, MdDescription } from "react-icons/md";

const StaffViewModal = ({ isOpen, onClose, staffMember, campus }) => {
  if (!isOpen || !staffMember) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatSalary = (salary) => {
    if (!salary) return "—";
    return `PKR ${Number(salary).toLocaleString()}`;
  };

  return (
    <div className="staff-view-modal-overlay" onClick={onClose}>
      <div className="staff-view-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="staff-view-modal-header">
          <h2>Staff Details</h2>
          <button className="staff-view-modal-close" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="staff-view-modal-body">
          {/* Profile Header */}
          <div className="staff-view-profile-header">
            <div className="staff-view-avatar">
              <span>{staffMember.name?.charAt(0)?.toUpperCase() || "?"}</span>
            </div>
            <div className="staff-view-profile-info">
              <h3>{staffMember.name}</h3>
              <span className="staff-view-designation-badge">{staffMember.designation || "No Designation"}</span>
              <p className="staff-view-staff-id">Staff ID: {staffMember.staff_id || staffMember.id || "—"}</p>
            </div>
          </div>

          {/* Details Grid */}
          <div className="staff-view-details-grid">
            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdEmail /></div>
              <div>
                <label>Email</label>
                <p>{staffMember.email || "—"}</p>
              </div>
            </div>

            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdPhone /></div>
              <div>
                <label>Phone</label>
                <p>{staffMember.phone || "—"}</p>
              </div>
            </div>

            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdDescription /></div>
              <div>
                <label>CNIC</label>
                <p>{staffMember.cnic || "—"}</p>
              </div>
            </div>

            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdWork /></div>
              <div>
                <label>Department</label>
                <p>{staffMember.department || "—"}</p>
              </div>
            </div>

            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdPerson /></div>
              <div>
                <label>Gender</label>
                <p>{staffMember.gender || "—"}</p>
              </div>
            </div>

            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdCalendarToday /></div>
              <div>
                <label>Joining Date</label>
                <p>{formatDate(staffMember.joining_date)}</p>
              </div>
            </div>

            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdAttachMoney /></div>
              <div>
                <label>Salary</label>
                <p>{formatSalary(staffMember.salary)}</p>
              </div>
            </div>

            <div className="staff-view-detail-item">
              <div className="staff-view-detail-icon"><MdHome /></div>
              <div>
                <label>Address</label>
                <p>{staffMember.address || "—"}</p>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          {(staffMember.emergency_contact_name || staffMember.emergency_contact_number) && (
            <div className="staff-view-emergency-section">
              <h4>Emergency Contact</h4>
              <div className="staff-view-emergency-grid">
                <div>
                  <label>Name</label>
                  <p>{staffMember.emergency_contact_name || "—"}</p>
                </div>
                <div>
                  <label>Number</label>
                  <p>{staffMember.emergency_contact_number || "—"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Documents Section */}
          {staffMember.documents && staffMember.documents.length > 0 && (
            <div className="staff-view-documents-section">
              <h4>Documents ({staffMember.documents.length})</h4>
              <div className="staff-view-documents-list">
                {staffMember.documents.map((doc, index) => (
                  <div key={doc.id || index} className="staff-view-document-item">
                    <span className="staff-view-document-name">{doc.name || `Document ${index + 1}`}</span>
                    <span className="staff-view-document-type">{doc.type || "Other"}</span>
                    <span className="staff-view-document-file">{doc.file_name || "—"}</span>
                    <span className="staff-view-document-date">{formatDate(doc.uploaded_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="staff-view-modal-footer">
            <button className="staff-view-close-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .staff-view-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 27, 45, 0.65);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: staffViewFadeIn 0.25s ease;
        }

        @keyframes staffViewFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .staff-view-modal-container {
          background: #ffffff;
          border-radius: 16px;
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.3);
          animation: staffViewSlideIn 0.3s ease;
        }

        @keyframes staffViewSlideIn {
          from {
            transform: translateY(-30px) scale(0.98);
            opacity: 0;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        .staff-view-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 28px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 16px 16px 0 0;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .staff-view-modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
          color: #0f1b2d;
        }

        .staff-view-modal-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #64748b;
          font-size: 24px;
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .staff-view-modal-close:hover {
          background: #f1f5f9;
          color: #0f1b2d;
        }

        .staff-view-modal-body {
          padding: 28px;
        }

        .staff-view-profile-header {
          display: flex;
          align-items: center;
          gap: 20px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 20px;
        }

        .staff-view-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #0f1b2d;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .staff-view-profile-info h3 {
          margin: 0;
          font-size: 18px;
          color: #1a202c;
        }

        .staff-view-designation-badge {
          display: inline-block;
          padding: 2px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          background: #e8f0fe;
          color: #1d4ed8;
          margin-top: 4px;
        }

        .staff-view-staff-id {
          margin: 4px 0 0 0;
          font-size: 13px;
          color: #64748b;
        }

        .staff-view-details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }

        .staff-view-detail-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .staff-view-detail-icon {
          color: #0f1b2d;
          font-size: 18px;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .staff-view-detail-item label {
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .staff-view-detail-item p {
          margin: 2px 0 0 0;
          font-size: 14px;
          color: #1a202c;
          font-weight: 500;
        }

        .staff-view-emergency-section {
          padding: 16px;
          background: #fef2f2;
          border-radius: 8px;
          border: 1px solid #fecaca;
          margin-bottom: 20px;
        }

        .staff-view-emergency-section h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: #991b1b;
        }

        .staff-view-emergency-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .staff-view-emergency-grid label {
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .staff-view-emergency-grid p {
          margin: 2px 0 0 0;
          font-size: 14px;
          color: #1a202c;
          font-weight: 500;
        }

        .staff-view-documents-section {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .staff-view-documents-section h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: #1a202c;
        }

        .staff-view-documents-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .staff-view-document-item {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          gap: 8px;
          padding: 10px 14px;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          font-size: 13px;
        }

        .staff-view-document-name {
          font-weight: 600;
          color: #1a202c;
        }

        .staff-view-document-type {
          color: #1d4ed8;
          background: #e8f0fe;
          padding: 0 8px;
          border-radius: 4px;
          font-size: 11px;
          display: inline-block;
          text-align: center;
        }

        .staff-view-document-file {
          color: #64748b;
        }

        .staff-view-document-date {
          color: #64748b;
          font-size: 12px;
        }

        .staff-view-modal-footer {
          display: flex;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }

        .staff-view-close-btn {
          padding: 10px 32px;
          border: none;
          border-radius: 8px;
          background: #0f1b2d;
          color: #ffffff;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .staff-view-close-btn:hover {
          background: #1a3050;
        }

        /* Scrollbar */
        .staff-view-modal-container::-webkit-scrollbar {
          width: 8px;
        }

        .staff-view-modal-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        .staff-view-modal-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }

        .staff-view-modal-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Responsive */
        @media (max-width: 640px) {
          .staff-view-details-grid {
            grid-template-columns: 1fr;
          }

          .staff-view-emergency-grid {
            grid-template-columns: 1fr;
          }

          .staff-view-document-item {
            grid-template-columns: 1fr;
            gap: 4px;
          }

          .staff-view-modal-container {
            max-height: 100vh;
            border-radius: 0;
            max-width: 100%;
          }

          .staff-view-modal-header {
            padding: 16px 20px;
          }

          .staff-view-modal-body {
            padding: 20px;
          }

          .staff-view-profile-header {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default StaffViewModal;